import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import {
  getAgentStatusList,
  getDropoffStatusList,
} from "../../../features/agent/edit-agent-slice";
import EditPartnerBranchesModal from "./modals/edit-partner-branches-modal";
import StatusHistoryModal from "./modals/status-history-modal";
import IdStatusHistoryModal from "./modals/id-status-history";
import EditAgentStatusModal from "./modals/edit-agent-status-modal";
import VerifyIdModal from "./modals/verify-id-modal";
import DropoffStatusHistoryModal from "./modals/drop-off-history-modal";
import {
  addHoursToDate,
  formatDateTime,
  GetLastTenDaysToCurrent,
} from "../../../algorithms/date";
import axios from "axios";
import UploadAgentIdModal from "./modals/upload-agent-id-modal";

import ViewSubmitIdModal from "./modals/view-submit-id-modal";
import SendAppModal from "../broadcast-agent/components/send-app-modal";
import SendEmailModal from "../broadcast-agent/components/send-email-modal";
import sendApiRequest from "../../../api/broadcast-agent";
import { ContentContainer } from "../../../components/layout";
import {
  Breadcrumbs,
  Button,
  ImageViewer,
  Modal,
  Snackbar,
  IconText,
  DownloadButton,
} from "../../../components/core";
import {
  AGENT_STATUS_HISTORY,
  FETCH_AGENT_VERIFICATION_HISTORY,
  BRANCHES,
  GET_AGENT_BY_ID,
  PACKAGE_DROPOFF_STATUS_HISTORY,
  PARTNERS,
  SAVE_STATUS_HISTORY,
  UPDATE_AGENT,
} from "../../../constants/graphql/partner-agent-queries";

import { QUERY_SUSPICIOUS_NUMBERS } from "../../../constants/graphql/drop-off-graphql";
import {
  getStatusWithSuspendedCheck,
  getAgentStatusStyle,
  agentVerificationStatusClassName,
  agenVerificationStatusHelper,
} from "../../../utils/agent-helpers";
import { formatCurrencyValue } from "../../../utils/formatters";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Card } from "primereact/card";
import { Image } from "primereact/image";
import { Button as PrimereactBUtton } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import SuspisciousNumbersPerAgent from "../suspicious-agent-numbers/modals/sus-numbers-dialog";
import { Dialog } from "primereact/dialog";
import { Strings } from "../../../constants/strings";

const SkeletonBox = ({ className = "" }) => (
  <div
    className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded ${className}`}
    style={{ backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }}
  />
);

const SkeletonField = () => (
  <div className="flex flex-col gap-1">
    <SkeletonBox className="h-3 w-24" />
    <SkeletonBox className="h-5 w-40" />
  </div>
);

const ProfileSkeleton = () => (
  <div className="mt-4 p-3">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
      <div className="flex items-center gap-4">
        <SkeletonBox className="w-[80px] h-[80px] rounded-full shrink-0" />
        <div className="flex flex-col gap-2">
          <SkeletonBox className="h-6 w-48" />
          <SkeletonBox className="h-4 w-32" />
        </div>
      </div>
      <div className="flex flex-col sm:items-end gap-2">
        <SkeletonBox className="h-7 w-36 rounded-full" />
        <SkeletonBox className="h-4 w-48" />
      </div>
    </div>
    <hr className="mb-6" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div className="flex flex-col items-center gap-3">
        <SkeletonBox className="w-[280px] h-[260px] rounded-lg" />
        <SkeletonBox className="h-5 w-40" />
        <SkeletonBox className="h-4 w-52" />
        <SkeletonBox className="h-9 w-full rounded" />
        <div className="flex gap-2 w-full">
          <SkeletonBox className="h-9 flex-1 rounded" />
          <SkeletonBox className="h-9 flex-1 rounded" />
        </div>
      </div>
      <div className="flex flex-col gap-4 border-t md:border-t-0 pt-4 md:pt-0">
        <SkeletonBox className="h-4 w-36" />
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonField key={i} />
        ))}
      </div>
      <div className="flex flex-col gap-4 border-t md:border-t-0 pt-4 md:pt-0">
        <SkeletonBox className="h-4 w-36" />
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonField key={i} />
        ))}
      </div>
    </div>
  </div>
);

function formatDate(inputDate) {
  const options = { year: "numeric", month: "short", day: "numeric" };
  const date = new Date(inputDate);
  return date.toLocaleDateString("en-US", options);
}

function processAgentData(raw) {
  if (!raw) return null;
  const updated = { ...raw };
  updated.partnerId = raw.partner.partnerId;
  updated.partnerBranchId = raw.partnerBranch.partnerBranchId;
  updated.agentStatusComments = raw.agentStatusComments
    ? raw.agentStatusComments
    : "";
  updated.comments = raw.comments ? raw.comments : "";
  return updated;
}

export default function AgentProfile() {
  const fetchNumber = useQuery(QUERY_SUSPICIOUS_NUMBERS);
  const CURRENT_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
  const dt = useRef(null);

  const warningImage = (
    <img
      src={`${process.env.PUBLIC_URL}/assets/images/warning.png`}
      alt="Warning"
      style={{ width: "150px", height: "150px" }} // or whatever size you need
    />
  );

  const [suspiciousNumber, setsuspiciousNumber] = useState([]);
  useEffect(() => {
    if (fetchNumber.data) {
      var tempArr = [];
      fetchNumber.data.suspiciousAgents.forEach((d) => {
        tempArr.push(d.agentMobile);
      });
      setsuspiciousNumber(tempArr);
    }
  }, [fetchNumber.data]);

  //Redux and Utilities
  const [accessPermission] = useState(
    localStorage.getItem("permission")
      ? JSON.parse(localStorage.getItem("permission"))
      : {},
  );
  const client = useApolloClient();
  const { partnerAgentId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  //Local State
  const [agentData, setAgentData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    suffixName: "",
    agentMobile: "",
    agentEmail: "",
    birthDate: "",
    createdAt: "",
    verifiedOn: "",
    verifiedStatus: "",
    loadBalance: "",
    walletBalance: "",
    agentStatusComments: "",
    partnerId: -1,
    partnerBranchId: -1,
    comments: "",
    rejectedIdPics: null,
    agentStatus: "",
    idPhotoUrl: "",
  });
  const [partnerList, setPartnerList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const idStatusList = [
    "Unverified",
    "Verified",
    "Pending verification",
    "Rejected",
  ];
  const [openSaveModal, setOpenSaveModal] = React.useState(false);
  const [openCancelModal, setOpenCancelModal] = React.useState(false);
  const [snack, setSnack] = React.useState({
    isOpen: false,
    message: "",
    type: "success",
  });
  const [openStatusHistory, setOpenStatusHistory] = React.useState(false);
  const [openIdHistory, setOpenIdHistory] = React.useState(false);
  const [openSubmitId, setOpenSubmitId] = React.useState(false);
  const [openStatusModal, setOpenStatusModal] = React.useState(false);
  const [openEditPartnerBranchesModal, setOpenEditPartnerBranchesModal] =
    React.useState(false);
  const [openDropoffStatusHistory, setOpenDropoffStatusHistory] =
    React.useState(false);
  const [openSuspiciousModal, setOpenSuspiciousModal] = useState(false);
  const [openUploadAgentIdModal, setOpenUploadAgentIdModal] =
    React.useState(false);
  const [imageViewer, setImageViewer] = React.useState({
    url: "",
    open: false,
  });
  const [agentVerification, setAgentVerification] = React.useState({
    verifiedStatus: 2,
  });
  const [openViewSubmitId, setOpenViewSubmitId] = React.useState(false);

  const [sendEmailData, setSendEmailData] = React.useState({
    message: "",
    subject: "",
    operatorId: localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user")).operator_id
      : "",
    target: [],
    // target: [`${userId}@kenneth.p.galvez@gmail.com`],
  });

  const selectedData = [{ partnerAgentId: partnerAgentId }];
  const [searchCriteria, setSearchCriteria] = React.useState({
    operatorId: localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user")).operator_id
      : "",
    firstName: "",
    lastName: "",
    agentMobile: "",
    partnerId: [],
    partnerBranchId: "-2",
    withApp: "-2",
    agentStatus: "-2",
    verifiedStatus: "-2",
    signupDateFrom: "",
    signupDateInto: "",
  });
  const [sendAppData, setSendAppData] = React.useState({
    message: "",
    target: [],
    category: "-1",
    image: "",
    subject: "",
  });
  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  //Redux Selector
  const [limit, setlimit] = useState(10);
  //Queries
  const {
    data: getAgentById,
    refetch: refetchAgent,
    loading: agentLoading,
    error: agentError,
  } = useQuery(GET_AGENT_BY_ID, {
    variables: { agentId: parseInt(partnerAgentId) },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const partner = useQuery(PARTNERS, {
    variables: {
      operatorId: localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).operator_id
        : "",
    },
  });
  const branch = useQuery(BRANCHES, {
    variables: {
      operatorId: localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).operator_id
        : "",
    },
  });
  const {
    data: agentAccountStatusHistoryData,
    refetch: refetchAgentAccountStatusHistory,
    loading: agentAccountStatusHistoryLoading,
  } = useQuery(FETCH_AGENT_VERIFICATION_HISTORY, {
    variables: {
      partnerAgentId: parseInt(partnerAgentId),
      agentStatus: true,
      verifiedStatus: false,
    },
  });
  const {
    data: agentStatusHistoryData,
    refetch: refetchAgentStatusHistory,
    loading: agentStatusHistoryLoading,
  } = useQuery(FETCH_AGENT_VERIFICATION_HISTORY, {
    variables: {
      partnerAgentId: parseInt(partnerAgentId),
      agentStatus: false,
      verifiedStatus: true,
    },
  });
  const getDropoffStatus = useQuery(PACKAGE_DROPOFF_STATUS_HISTORY, {
    variables: {
      operatorId: localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).operator_id
        : "",
      partnerAgentId: partnerAgentId,
      // dateStart: GetLastTenDaysToCurrent(currentDate: new Date(), limit: 100)[100 - 1],
      dateStart: GetLastTenDaysToCurrent(limit)[limit - 1],
      dateEnd: GetLastTenDaysToCurrent(1)[0],
    },
  });
  function combineDateAndTime(dateString, timeString) {
    // Combine date and time strings into a single string
    const combinedString = `${dateString}T${timeString}:00`;

    // Create a Date object from the combined string
    const combinedDateTime = new Date(combinedString);

    // Get the ISO 8601 format string with time and timezone
    const isoString = combinedDateTime.toISOString();

    // Remove milliseconds and return the formatted string
    return isoString.slice(0, -5) + "Z";
  }
  useEffect(() => {
    if (getDropoffStatus.data) {
      dispatch(getDropoffStatusList(getDropoffStatus.data));
    }
  }, [limit]);

  //Mutations
  const [updateAgent] = useMutation(UPDATE_AGENT);
  const [saveStatusHistory] = useMutation(SAVE_STATUS_HISTORY);
  const [newDate, setNewDate] = React.useState(new Date());
  //Bread crumbs items
  const breadcrumbItems = [
    { label: "Agent", path: `/agents` },
    { label: "Agent Profile", path: "#" },
  ];

  //Event and Function triggers
  const handleOnChanged = (event) => {
    const { name, value } = event.target;
    setAgentData((prev) => {
      switch (name) {
        case "partner":
          return { ...prev, partnerId: value };
        case "branch":
          return { ...prev, partnerBranchId: value };
        case "rejectedIdPics":
          return { ...prev, rejectedIdPics: [value] };
        case "agentStatus":
          return { ...prev, agentStatus: value };
        case "agentStatusComments":
          return { ...prev, agentStatusComments: value };
        case "comments":
          return { ...prev, comments: value };
        case "statusDuration":
          return { ...prev, statusDuration: value };
        default:
          return prev;
      }
    });
  };

  const handleSaveChanges = () => {
    if (agentData.partnerId === "-1" || agentData.partnerBranchId === "-1") {
      setSnack({
        isOpen: true,
        message: "Please add data on required fields",
        type: "error",
      });
    } else {
      updateAgent({
        variables: {
          operatorId: localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")).operator_id
            : "",
          partnerAgentId: parseInt(partnerAgentId),
          partnerId: parseInt(agentData.partnerId),
          partnerBranchId: parseInt(agentData.partnerBranchId),
        },
      }).then((data) => {
        setAgentData(processAgentData(data.data.updateAgent.partnerAgent));
      });
      setSnack({
        isOpen: true,
        message: "Successfully modified Agent's Profile",
        type: "success",
      });
      setOpenEditPartnerBranchesModal(false);
    }
    setOpenSaveModal(false);
    client.clearStore();
  };
  const handleCancelChanges = () => {
    // getAgentById.refetch({ agentId: parseInt(partnerAgentId) }).then((data) => {
    // 	dispatch(getAgentData(data.data.getAgentById[0]));
    // 	setSnack({
    // 		isOpen: true,
    // 		message: "Operation cancelled sucessfully.",
    // 		type: "success",
    // 	});
    // });
    // dispatch(getAgentData(getAgentById.data.getAgentById[0]))
    setOpenCancelModal(false);
  };

  const handleAcceptStatus = () => {
    const apiLink = `${process.env.REACT_APP_website_api}v1/website/operator/agent/profile/id/verify`;
    const corsHeaders = {
      // Add CORS headers here if required by your server configuration
      // For example:
      // 'Access-Control-Allow-Origin': 'https://your-frontend-domain.com',
      // 'Access-Control-Allow-Methods': 'POST',
      // 'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    };
    var requestData = {
      operator_id: JSON.parse(localStorage.getItem("user")).operator_id,
      partner_agent_id: partnerAgentId,
      agent_mobile: agentData.agentMobile,
      verified_status: 1,
      comments: agentData.agentStatusComments,
    };

    requestData.verified_status = 1;
    setAgentVerification((prev) => ({
      verifiedStatus: 1,
    }));
    updateAgent({
      variables: {
        operatorId: localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user")).operator_id
          : "",
        partnerAgentId: parseInt(partnerAgentId),
        verifiedStatus: 1,
        rejectedIdPics: Array.isArray(agentData.rejectedIdPics)
          ? agentData.rejectedIdPics.flat() // This flattens nested arrays
          : agentData.rejectedIdPics || [],
      },
    }).then((data) => {
      setAgentData(processAgentData(data.data.updateAgent.partnerAgent));
    });
    saveStatusHistory({
      variables: {
        agentStatusHistoryArgs: [
          {
            partnerAgentId: parseInt(partnerAgentId),
            agentStatus: null,
            verifiedStatus: 1,
            agentStatusComments:
              agentData.agentStatusComments !== ""
                ? agentData.agentStatusComments
                : "N/A",
            comments: agentData.comments !== "" ? agentData.comments : "N/A",
          },
        ],
      },
    }).then(() => {
      refetchAgentStatusHistory({
        partnerAgentId: parseInt(partnerAgentId),
      }).then((data) => {
        dispatch(
          getAgentStatusList({ type: "verifiedStatus", data: data.data }),
        );
      });
    });
    setOpenSubmitId(false);
    setSnack({
      isOpen: true,
      message: "Successfully accept agent",
      type: "success",
    });

    axios
      .post(apiLink, requestData, { headers: corsHeaders })
      .then((response) => {
        // Handle the response here
        // console.log('Response:', response.data);
      })
      .catch((error) => {
        // Handle any errors here
        // console.error('Error:', error);
      });
  };

  const handleRejectStatus = () => {
    const apiLink = `${process.env.REACT_APP_website_api}v1/website/operator/agent/profile/id/verify`;
    const corsHeaders = {
      // Add CORS headers here if required by your server configuration
      // For example:
      // 'Access-Control-Allow-Origin': 'https://your-frontend-domain.com',
      // 'Access-Control-Allow-Methods': 'POST',
      // 'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    };
    var requestData = {
      operator_id: JSON.parse(localStorage.getItem("user")).operator_id,
      partner_agent_id: partnerAgentId,
      agent_mobile: agentData.agentMobile,
      verified_status: 1,
      comments: agentData.agentStatusComments,
    };
    requestData.verified_status = 3;
    setAgentVerification((prev) => ({
      verifiedStatus: 3,
    }));
    updateAgent({
      variables: {
        operatorId: localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user")).operator_id
          : "",
        // rejectedIdPics:
        // 	agentData.rejectedIdPics.length !== 0
        // 		? agentData.rejectedIdPics
        // 		: [],
        rejectedIdPics: Array.isArray(agentData.rejectedIdPics)
          ? agentData.rejectedIdPics.flat() // This flattens nested arrays
          : agentData.rejectedIdPics || [],
        partnerAgentId: parseInt(partnerAgentId),
        verifiedStatus: 3,
      },
    }).then((data) => {
      setAgentData(processAgentData(data.data.updateAgent.partnerAgent));
    });
    saveStatusHistory({
      variables: {
        agentStatusHistoryArgs: [
          {
            partnerAgentId: parseInt(partnerAgentId),
            agentStatus: null,
            verifiedStatus: 3,
            agentStatusComments:
              agentData.agentStatusComments !== ""
                ? agentData.agentStatusComments
                : "N/A",
            comments: agentData.comments !== "" ? agentData.comments : "N/A",
          },
        ],
      },
    }).then(() => {
      refetchAgentStatusHistory({
        partnerAgentId: parseInt(partnerAgentId),
      }).then((data) => {
        dispatch(
          getAgentStatusList({ type: "verifiedStatus", data: data.data }),
        );
      });

      // window.location.reload();
    });
    setOpenSubmitId(false);
    setSnack({
      isOpen: true,
      message: "Successfully reject agent",
      type: "success",
    });

    axios
      .post(apiLink, requestData, { headers: corsHeaders })
      .then((response) => {
        // Handle the response here
        // console.log('Response:', response.data);
      })
      .catch((error) => {
        // Handle any errors here
        // console.error('Error:', error);
      });
  };
  const handleUploadAgentId = (event) => {
    // UploadAgentIdModal is currently unused
  };

  const [initialVerifiedStatus, setInitialVerifiedStatus] = useState(0);

  //React useEffects
  React.useEffect(() => {
    if (getAgentById) {
      setInitialVerifiedStatus(getAgentById.getAgentById[0].agentStatus);
      // setAgentFetchedComment(getAgentById.getAgentById[0].comments);
      setAgentData(processAgentData(getAgentById.getAgentById[0]));
    }
  }, [getAgentById]);
  console.log({ agentData });

  React.useEffect(() => {
    if (partner.data) {
      const tempVar = [];
      if (partner.data.partners)
        partner.data.partners.forEach((element) => {
          tempVar.push({
            value: element.partnerId,
            name: element.companyNickname,
          });
        });
      setPartnerList(tempVar);
    }
  }, [partner.data]);
  React.useEffect(() => {
    if (branch.data) {
      const tempVar = [];
      if (branch.data.partnerBranches)
        branch.data.partnerBranches.forEach((element) => {
          tempVar.push({
            value: element.partnerBranchId,
            name: element.partnerBranch,
          });
        });
      setBranchList(tempVar);
    }
  }, [branch.data]);
  React.useEffect(() => {
    if (agentStatusHistoryData)
      dispatch(
        getAgentStatusList({
          type: "verifiedStatus",
          data: agentStatusHistoryData,
        }),
      );
  }, [agentStatusHistoryData]);
  React.useEffect(() => {
    if (agentAccountStatusHistoryData)
      dispatch(
        getAgentStatusList({
          type: "agentStatus",
          data: agentAccountStatusHistoryData,
        }),
      );
  }, [agentAccountStatusHistoryData]);
  React.useEffect(() => {
    if (agentData.partnerId !== "-1")
      branch
        .refetch({
          operatorId: localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")).operator_id
            : "",
          partnerId: agentData.partnerId,
        })
        .then((data) => {});
    else
      branch
        .refetch({
          operatorId: localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")).operator_id
            : "",
        })
        .then((data) => {});
  }, [agentData.partnerId]);

  console.log({ agentData });
  React.useEffect(() => {
    if (getDropoffStatus.data) {
      dispatch(getDropoffStatusList(getDropoffStatus.data));
    }
  }, [getDropoffStatus.data]);

  const refetchAll = () => {
    refetchAgent();
    refetchAgentAccountStatusHistory();
    refetchAgentStatusHistory();
  };

  function omitEmptyAndMinusOneFields(obj) {
    const result = {};
    for (const key in obj) {
      // Exclude null, undefined, empty string, and -1 values
      if (
        obj[key] !== null &&
        obj[key] !== undefined &&
        obj[key] !== "" &&
        parseInt(obj[key]) !== -1 && // Check for -1
        parseInt(obj[key]) !== -2 &&
        !(Array.isArray(obj[key]) && obj[key].length === 0) // Check for empty array
      ) {
        result[key] = obj[key];
      }
    }
    return result;
  }

  const status = getStatusWithSuspendedCheck(agentData);

  // Computed values for redesigned layout
  const fullName = [
    agentData.firstName,
    agentData.middleName,
    agentData.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .concat(agentData.suffixName ? `, ${agentData.suffixName}` : "");

  const isSuspicious = agentData.suspiciousMobileCheck?.isSuspicious;

  // const signupDetailsObj = (() => {
  //   try {
  //     if (!agentData.signupDetails || agentData.signupDetails.trim() === "")
  //       return null;
  //     const parsed = JSON.parse(agentData.signupDetails);
  //     return parsed && typeof parsed === "object" ? parsed : null;
  //   } catch {
  //     return null;
  //   }
  // })();
  const signupDetails = useMemo(() => {
    if (!agentData?.signupDetails) return null;

    const removeAffiliationKeys = (value) => {
      if (Array.isArray(value)) {
        return value.map(removeAffiliationKeys);
      }

      if (value && typeof value === "object") {
        return Object.entries(value).reduce((acc, [key, nestedValue]) => {
          if (/(affiliation|affiliate)/i.test(key)) return acc;
          if (["request_by", "request_source", "employee_id"].includes(key)) {
            return acc;
          }
          acc[key] = removeAffiliationKeys(nestedValue);
          return acc;
        }, {});
      }

      return value;
    };

    try {
      const parsed = JSON.parse(agentData.signupDetails);
      if (!parsed || typeof parsed !== "object") return null;
      return removeAffiliationKeys(parsed);
    } catch {
      return null;
    }
  }, [agentData?.signupDetails]);

  const partnerName =
    partnerList.find((x) => x.value === agentData.partnerId)?.name || "N/A";
  const branchName =
    branchList.find((x) => x.value === agentData.partnerBranchId)?.name ||
    "N/A";
  const statusStyle = getAgentStatusStyle(
    agentData.agentStatus,
    agentData.comments,
    agentData.agentStatusComments,
  );
  const profileImageSrc = `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo1.jpg?${newDate.getTime()}`;

  console.log({ agentData });
  return (
    <div className="w-full">
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div className="mb-5"></div>
      <Breadcrumbs items={breadcrumbItems} />
      <div className="mt-2"></div>
      <ContentContainer>
        {agentLoading ? (
          <ProfileSkeleton />
        ) : (
          <Card className="mt-4 p-3 text-black bg-slate-50">
            {/* ── TOP SECTION ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
              {/* Avatar + Name */}
              <div className="flex items-center gap-4">
                <Image
                  src={profileImageSrc}
                  alt="Agent Profile"
                  imageClassName="rounded-full w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] object-cover"
                  className="hidden sm:block rounded-full border-2 border-primary-primaryYellow shrink-0"
                  preview
                />
                <div className="min-w-0">
                  {isSuspicious && (
                    <div className="text-xs font-semibold text-red-500 mb-0.5 flex items-center gap-1">
                      <i className="pi pi-flag-fill text-xs" />
                      Suspicious Courier
                      {agentData.suspiciousMobileCheck?.linkedAgents?.length >
                        0 && (
                        <span className="font-normal text-red-400">
                          {" "}
                          — flagged by{" "}
                          {
                            agentData.suspiciousMobileCheck.linkedAgents.length
                          }{" "}
                          agent
                          {agentData.suspiciousMobileCheck.linkedAgents.length >
                          1
                            ? "s"
                            : ""}
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    className={`text-base sm:text-xl font-bold leading-tight break-words ${
                      isSuspicious ? "text-red-600" : ""
                    }`}
                  >
                    {`${agentData.lastName}, ${agentData.firstName} ${agentData.middleName || ""} ${agentData.suffixName || ""}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    Employee ID: {agentData.employeeId || "N/A"}
                  </div>
                </div>
              </div>

              {/* Status badge + history links */}
              <div className="flex flex-col sm:items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Agent Status:</span>
                  <div
                    className={`flex items-center gap-1 ${statusStyle.bg} border ${statusStyle.border} rounded-full px-3 py-0.5`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${statusStyle.dot} inline-block`}
                    />
                    <span
                      className={`font-semibold text-sm ${statusStyle.text}`}
                    >
                      {getStatusWithSuspendedCheck(
                        agentData.agentStatus,
                        agentData.comments,
                      )}{" "}
                    </span>
                    <i
                      className={`fas fa-edit cursor-pointer ml-1 text-xs ${
                        statusStyle.text
                      } ${
                        accessPermission.agents === 1 ||
                        accessPermission.agents === "1"
                          ? "hidden"
                          : ""
                      }`}
                      onClick={() => setOpenStatusModal(true)}
                    />
                  </div>
                </div>
                <div className="flex flex-row gap-3 text-sm items-center flex-wrap">
                  <button
                    className="hover:underline hover:text-primary-primaryYellow text-xs italic flex items-center gap-1"
                    onClick={() => setOpenStatusHistory(true)}
                  >
                    <i className="pi pi-history text-xs" /> Agent Status History
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    className="hover:underline hover:text-primary-primaryYellow text-xs italic flex items-center gap-1"
                    onClick={() => setOpenDropoffStatusHistory(true)}
                  >
                    <i className="pi pi-history text-xs" /> Dropoffs History
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    className={`hover:underline text-xs italic flex items-center gap-1 ${
                      agentData.suspiciousMobiles?.length > 0
                        ? "text-red-500 hover:text-red-600 font-semibold"
                        : "hover:text-primary-primaryYellow"
                    }`}
                    onClick={() => setOpenSuspiciousModal(true)}
                  >
                    <i
                      className={`pi ${
                        agentData.suspiciousMobiles?.length > 0
                          ? "pi-flag-fill"
                          : "pi-flag"
                      } text-xs`}
                    />{" "}
                    Suspicious Numbers
                    {agentData.suspiciousMobiles?.length > 0 && (
                      <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                        {agentData.suspiciousMobiles.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <Divider />

            {/* ── MIDDLE SECTION ── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Col 1 — Photo + verify + actions */}
              <div className="flex flex-col items-center gap-3">
                <div className="flex justify-center">
                  <Image
                    src={profileImageSrc}
                    alt="ID Profile"
                    onClick={() => setOpenViewSubmitId(true)}
                    className="cursor-pointer"
                    imageClassName="w-[280px] object-fill shadow-lg outline outline-6 outline-stone-600 transition duration-300"
                  />
                </div>

                {agentData.verifiedStatus === 1 ? (
                  <>
                    <div className="text-sm text-green-600 font-semibold text-center">
                      Verified
                      {agentData.verifiedOn
                        ? ` on ${formatDate(
                            agentData.verifiedOn.split("T")[0],
                          )}`
                        : ""}
                    </div>
                    {agentData.verifiedByAccount && (
                      <div className="text-center text-gray-400 text-sm italic">
                        Verified by: {agentData.verifiedByAccount.username} (
                        {agentData.verifiedByAccount.userEmail})
                      </div>
                    )}
                  </>
                ) : agentData.verifiedStatus === 3 ? (
                  <>
                    <div className="text-sm text-red-500 font-semibold text-center">
                      Rejected
                      {agentData.verifiedOn
                        ? ` on ${formatDate(
                            agentData.verifiedOn.split("T")[0],
                          )}`
                        : ""}
                    </div>
                    {agentData.verifiedByAccount && (
                      <div className="text-center text-gray-400 text-sm italic">
                        Rejected by: {agentData.verifiedByAccount.username} (
                        {agentData.verifiedByAccount.userEmail})
                      </div>
                    )}
                  </>
                ) : agentData.verifiedStatus === 2 ? (
                  <PrimereactBUtton
                    label="CLICK TO VERIFY IDs"
                    className="bg-primary-primaryYellow uppercase tracking-normal rounded-md w-full h-12 mx-auto font-semibold"
                    onClick={() => setOpenSubmitId(true)}
                    disabled={
                      accessPermission.agents === 1 ||
                      accessPermission.agents === "1"
                    }
                  />
                ) : (
                  <p className="font-semibold text-center italic text-sm text-gray-500">
                    {idStatusList[agentData.verifiedStatus] || "Unverified"}
                  </p>
                )}

                <div className="mx-auto">
                  <IconText
                    className="underline"
                    text="Verification History"
                    iconUrl={`${process.env.PUBLIC_URL}/assets/images/history_icon.png`}
                    onClick={() => setOpenIdHistory(true)}
                  />
                </div>

                <div className="flex gap-2 w-full">
                  <PrimereactBUtton
                    label="SEND EMAIL"
                    className="flex-1 uppercase border border-primary-primaryYellow rounded-md h-12 px-2 hover:bg-gray-200 text-sm font-semibold bg-white"
                    disabled={
                      agentData?.agentEmail === "" ||
                      accessPermission.agents === 1 ||
                      accessPermission.agents === "1"
                    }
                    onClick={() =>
                      setSendEmailData((prev) => ({ ...prev, open: true }))
                    }
                  />
                  <PrimereactBUtton
                    label="SEND APP MSG"
                    className="flex-1 uppercase border border-primary-primaryYellow rounded-md h-12 px-2 hover:bg-gray-200 text-sm font-semibold bg-white"
                    disabled={
                      accessPermission.agents === 1 ||
                      accessPermission.agents === "1"
                    }
                    onClick={() =>
                      setSendAppData((prev) => ({ ...prev, open: true }))
                    }
                  />
                </div>
              </div>

              {/* Col 2 — Personal Details */}
              <div className="flex flex-col gap-4 border-t md:border-t-0 pt-4 md:pt-0">
                <div className="text-base font-semibold text-gray-400 uppercase tracking-wider">
                  Personal Details
                </div>
                {[
                  {
                    label: "Agent Mobile",
                    value: agentData.agentMobile
                      ? `+${agentData.agentMobile}`
                      : "N/A",
                  },
                  {
                    label: "Email Address",
                    value: agentData.agentEmail || "N/A",
                  },
                  {
                    label: "Birthdate",
                    value: agentData.birthDate
                      ? formatDate(agentData.birthDate)
                      : "N/A",
                  },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-sm text-gray-400">{label}</div>
                    <div className="font-semibold text-base">{value}</div>
                  </div>
                ))}

                <Divider className="border border-gray-200" />

                <div>
                  <div className="text-sm text-gray-400">Sign up Date</div>
                  <div className="font-semibold text-base">
                    {agentData.createdAt
                      ? formatDate(agentData.createdAt.split("T")[0])
                      : "N/A"}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400">Partner</div>
                  <div className="font-bold flex items-center gap-2">
                    <div className="font-bold flex items-center gap-2">
                      {signupDetails?.company
                        ? "Others"
                        : agentData?.partner?.companyNickname || "N/A"}
                    </div>{" "}
                  </div>
                </div>
                {signupDetails?.company && (
                  <div>
                    <div className="text-sm text-gray-400">Others</div>
                    <div className="font-bold">
                      {signupDetails.company || "N/A"}
                    </div>
                  </div>
                )}

                {/* <div>
                  <div className="text-sm text-gray-400">Partner</div>
                  <div className="font-semibold text-base flex items-center gap-1.5">
                    {signupDetailsObj ? "Others" : partnerName}
                    <i
                      className={`fas fa-edit cursor-pointer text-gray-400 hover:text-primary-primaryYellow text-xs ${
                        accessPermission.agents === 1 ||
                        accessPermission.agents === "1"
                          ? "hidden"
                          : ""
                      }`}
                      onClick={() => setOpenEditPartnerBranchesModal(true)}
                    />
                  </div>
                </div>

                {signupDetailsObj && (
                  <div>
                    <div className="text-sm text-gray-400">Others</div>
                    <div className="font-semibold text-base">
                      {signupDetailsObj.company || "N/A"}
                    </div>
                  </div>
                )} */}

                <div>
                  <div className="text-sm text-gray-400">Branches</div>
                  <div className="font-semibold text-base">{branchName}</div>
                </div>
              </div>

              {/* Col 3 — Account Details */}
              <div className="flex flex-col gap-4 border-t md:border-t-0 pt-4 md:pt-0">
                <div className="text-base font-semibold text-gray-400 uppercase tracking-wider">
                  Account Details
                </div>

                <div>
                  <div className="text-sm text-gray-400">Verified Status</div>
                  <div className="font-semibold text-base flex items-center gap-2 mt-0.5">
                    <Tag
                      className={agentVerificationStatusClassName(
                        agentData.verifiedStatus,
                      )}
                    >
                      {agenVerificationStatusHelper(agentData.verifiedStatus)}
                    </Tag>
                    {agentData.verifiedOn && (
                      <span className="font-normal text-sm text-gray-500">
                        on {formatDate(agentData.verifiedOn.split("T")[0])}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400">
                    Affiliation Status
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Tag
                      value={agentData.affiliationStatus || "Unaffiliated"}
                      className={`text-xs font-semibold ${
                        agentData.affiliationStatus === "Affiliated"
                          ? "bg-green-100 text-green-700"
                          : agentData.affiliationStatus ===
                              "Pending Agent Confirmation"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400">Wallet Balance</div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="font-semibold text-base">
                      {formatCurrencyValue(agentData.walletBalance)}
                    </span>
                    <Button
                      value="Top Up"
                      className="bg-gray-100 border border-gray-300 text-sm font-semibold py-1 px-3 rounded h-fit"
                      disabled={true}
                    />
                  </div>
                </div>

                {agentData.suspiciousMobiles?.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400 flex items-center gap-1.5 mb-1.5">
                      <i className="pi pi-flag text-red-400 text-xs" />
                      Flagged Suspicious Numbers
                      <span className="bg-red-100 text-red-600 text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                        {agentData.suspiciousMobiles.length}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {agentData.suspiciousMobiles.map((mob) => (
                        <div
                          key={mob.partnerAgentSuspiciousMobileId}
                          className="flex items-center justify-between bg-red-50 border border-red-100 rounded px-2.5 py-1.5"
                        >
                          <span className="text-sm font-semibold text-red-700">
                            +{mob.agentMobile}
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {new Date(mob.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      className="text-xs text-red-400 hover:text-red-600 hover:underline mt-1"
                      onClick={() => setOpenSuspiciousModal(true)}
                    >
                      Manage suspicious numbers →
                    </button>
                  </div>
                )}

                {/* Linked User Accounts */}
                {agentData?.relatedUserAccounts?.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400">User Account(s)</div>
                    <div className="flex flex-col gap-1 mt-1">
                      {agentData.relatedUserAccounts.map((user) => (
                        <div key={user.userId} className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <div
                              onClick={() => {
                                console.log({ user });
                                window.open(
                                  `/operator/admin/locker-user-accounts/user-profile-page/${user.userId}`,
                                  "_blank",
                                );
                              }}
                              // href={`${window.location.origin}/locker-user-accounts/user-profile/${user.userId}`}
                              className="font-semibold text-sm text-blue-600 flex items-center gap-1.5 cursor-pointer hover:underline w-fit"
                            >
                              <i className="pi pi-user text-xs" />
                              {user.userMobile}
                              <i className="pi pi-external-link text-xs text-blue-400" />
                            </div>
                            {/* <a
                              href={`${window.location.origin}/locker-user-accounts/user-profile/${user.userId}`}
                              target="_blank"
                              rel="noreferrer"
                            ></a> */}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span
                              className={`w-1.5 h-1.5 rounded-full inline-block ${
                                user.userStatus === 1
                                  ? "bg-green-500"
                                  : user.userStatus === -1 &&
                                      user.comments
                                        ?.toLowerCase()
                                        .includes("suspended")
                                    ? "bg-orange-400"
                                    : user.userStatus === -1
                                      ? "bg-red-400"
                                      : "bg-gray-400"
                              }`}
                            />
                            <span
                              className={`text-xs font-medium ${
                                user.userStatus === 1
                                  ? "text-green-600"
                                  : user.userStatus === -1 &&
                                      user.comments
                                        ?.toLowerCase()
                                        .includes("suspended")
                                    ? "text-orange-500"
                                    : user.userStatus === -1
                                      ? "text-red-500"
                                      : "text-gray-500"
                              }`}
                            >
                              {getStatusWithSuspendedCheck(
                                user.userStatus,
                                user.comments,
                              )}{" "}
                              {/* ✅ user directly */}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </ContentContainer>

      {/* ----- This are the empty modals */}
      <>
        <EditPartnerBranchesModal
          isOpen={openEditPartnerBranchesModal}
          onClose={() => setOpenEditPartnerBranchesModal(false)}
          partnerAgentId={partnerAgentId}
          agentData={agentData}
          onSuccess={() => {
            setOpenEditPartnerBranchesModal(false);
            refetchAll();
          }}
        />
        <StatusHistoryModal
          data={agentData}
          isOpen={openStatusHistory}
          statusHistory={agentAccountStatusHistoryData?.agentStatusHistory}
          onClose={() => setOpenStatusHistory(false)}
        />
        <IdStatusHistoryModal
          isOpen={openIdHistory}
          onClose={() => setOpenIdHistory(false)}
        />
        <EditAgentStatusModal
          isOpen={openStatusModal}
          onClose={() => setOpenStatusModal(false)}
          agentData={agentData}
          onSuccess={() => {
            setOpenStatusModal(false);
            refetchAll();
            // getAgentById.refetch({ agentId: parseInt(partnerAgentId) });
          }}
        />
        <SuspisciousNumbersPerAgent
          open={openSuspiciousModal}
          onHide={() => {
            setOpenSuspiciousModal(false);
          }}
          accessPermission={accessPermission}
          suspiciousData={agentData}
          isOnAgentProfile={true}
        />
        <VerifyIdModal
          open={openSubmitId}
          onHide={() => setOpenSubmitId(false)}
          agentData={agentData}
          partnerAgentId={partnerAgentId}
          submittedIds={[
            {
              src: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo1.jpg?${newDate.getTime()}`,
              alt: "Agent Photo / Selfie",
            },
            {
              src: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo2.jpg?${newDate.getTime()}`,
              alt: "Agent Selfie with ID",
            },
            {
              src: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo3.jpg?${newDate.getTime()}`,
              alt: "Courier Company ID (Front)",
            },
            {
              src: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo4.jpg?${newDate.getTime()}`,
              alt: "Courier Company ID (Back)",
            },
          ]}
          onSuccess={() => {
            refetchAll();
          }}
        />
        <DropoffStatusHistoryModal
          isOpen={openDropoffStatusHistory}
          onClose={() => setOpenDropoffStatusHistory(false)}
          limit={limit}
          setLimit={setlimit}
        />
        {/* <UploadAgentIdModal
					isOpen={openUploadAgentIdModal}
					agentData={agentData}
					partnerList={partnerList}
					onClose={() => setOpenUploadAgentIdModal(false)}
					setImageViewer={setImageViewer}
					partnerAgentId={partnerAgentId}
					onAccept={handleAcceptStatus}
					onReject={handleRejectStatus}
					handleOnChanged={handleUploadAgentId}
				/> */}
        <ViewSubmitIdModal
          open={openViewSubmitId}
          ids={[
            `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo1.jpg?${newDate.getTime()}`,
            `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo2.jpg?${newDate.getTime()}`,
            `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo3.jpg?${newDate.getTime()}`,
            `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo4.jpg?${newDate.getTime()}`,
          ]}
          images={[
            {
              itemImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo1.jpg?${newDate.getTime()}`,
              thumbnailImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo1.jpg?${newDate.getTime()}`,
              alt: "Image 1",
              title: "Title 1",
            },
            {
              itemImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo2.jpg?${newDate.getTime()}`,
              thumbnailImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo2.jpg?${newDate.getTime()}`,
              alt: "Image 2",
              title: "Title 2",
            },
            {
              itemImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo3.jpg?${newDate.getTime()}`,
              thumbnailImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo3.jpg?${newDate.getTime()}`,
              alt: "Image 3",
              title: "Title 3",
            },
            {
              itemImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo4.jpg?${newDate.getTime()}`,
              thumbnailImageSrc: `https://mbox-locker-${CURRENT_ENVIRONMENT}.s3.ap-southeast-1.amazonaws.com/image/agent/profile/${partnerAgentId}/id_photo4.jpg?${newDate.getTime()}`,
              alt: "Image 4",
              title: "Title 4",
            },
          ]}
          onClose={() => {
            setOpenViewSubmitId(false);
          }}
        />
        <Modal
          icon={`${process.env.PUBLIC_URL}/assets/images/check.png`}
          title="Save Changes"
          message="Are you sure you want to save all changes?"
          open={openSaveModal}
          actions={
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => setOpenSaveModal(false)}
                value={"No"}
                className={"p-2"}
              />
              <Button
                onClick={() => handleSaveChanges()}
                value={"Yes"}
                className={"p-2"}
              />
            </div>
          }
        />
        <Modal
          icon={`${process.env.PUBLIC_URL}/assets/images/error.png`}
          title="Cancel Changes"
          message="Are you sure you want to cancel all changes?"
          open={openCancelModal}
          actions={
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => setOpenCancelModal(false)}
                value={"No"}
                className={"p-2"}
              />
              <Button
                onClick={() => handleCancelChanges()}
                value={"Yes"}
                className={"p-2"}
              />
            </div>
          }
        />
        <ImageViewer
          imageUrl={imageViewer.url}
          onOpen={imageViewer.open}
          onClose={() => setImageViewer((prev) => ({ ...prev, open: false }))}
        />
      </>

      <SendAppModal
        header={`App Message Notification`}
        open={sendAppData.open}
        data={sendAppData}
        selectedData={selectedData}
        onChange={(e) => {
          const { name, value } = e.target;
          setSendAppData((prev) => ({ ...prev, [name]: value }));
        }}
        onSubmit={() => {
          let selectedAgentId = selectedData.map((data) => {
            return data.partnerAgentId;
          });
          let apiParameter = {
            operator_id: searchCriteria.operatorId,
            filter: omitEmptyAndMinusOneFields({
              partner_id: searchCriteria.partnerId,
              partner_branch_id: searchCriteria.partnerBranchId,
              agent_mobile: searchCriteria.agentMobile,
              agent_status: searchCriteria.agentStatus,
              verified_status: searchCriteria.verifiedStatus,
              first_name: searchCriteria.firstName,
              last_name: searchCriteria.lastName,
              signup_date_from: searchCriteria.signupDateFrom,
              signup_date_upto: searchCriteria.signupDateInto,
              with_app: searchCriteria.withApp,
            }),
            app_message: {
              subject: sendAppData.subject,
              content: sendAppData.message,
              image: sendAppData.image,
            },
            partner_agent_ids: selectedAgentId,
          };
          if (sendAppData.schedule && sendAppData.time) {
            apiParameter.schedule_at = combineDateAndTime(
              sendAppData.schedule,
              sendAppData.time,
            );
          }
          sendApiRequest(apiParameter)
            .then((data) => {
              setValidation({
                icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                title: "Success",
                message: "Successfully sent app message.",
                open: true,
                actions: (
                  <div className="gap-2 w-full mx-auto">
                    <Button
                      onClick={() => setValidation({ open: false })}
                      value={"OK"}
                      className={"p-2 w-full"}
                    />
                  </div>
                ),
              });
            })
            .catch((e) =>
              setValidation({
                icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                title: "Error",
                message: "Error sending app message",
                open: true,
                actions: (
                  <div className="gap-2 w-full mx-auto">
                    <Button
                      onClick={() => setValidation({ open: false })}
                      value={"OK"}
                      className={"p-2 w-full"}
                    />
                  </div>
                ),
              }),
            )
            .finally(() => {
              setSendAppData((prev) => ({ ...prev, open: false }));
            });
        }}
        onClose={() => {
          setSendAppData({
            open: false,
            operatorId: localStorage.getItem("user")
              ? JSON.parse(localStorage.getItem("user")).operator_id
              : "",
            message: "",
            target: [],
            category: -1,
            image: "",
            subject: "",
          });
        }}
      />
      <SendEmailModal
        header={`Email Message Notification`}
        open={sendEmailData.open}
        data={sendEmailData}
        selectedData={selectedData}
        onChange={(e) => {
          const { name, value } = e.target;
          setSendEmailData((prev) => ({ ...prev, [name]: value }));
        }}
        onSubmit={() => {
          let selectedAgentId = selectedData.map((data) => {
            return data.partnerAgentId;
          });
          let apiParameter = {
            operator_id: searchCriteria.operatorId,
            filter: omitEmptyAndMinusOneFields({
              partner_id: searchCriteria.partnerId,
              partner_branch_id: searchCriteria.partnerBranchId,
              agent_mobile: searchCriteria.agentMobile,
              agent_status: searchCriteria.agentStatus,
              verified_status: searchCriteria.verifiedStatus,
              first_name: searchCriteria.firstName,
              last_name: searchCriteria.lastName,
              signup_date_from: searchCriteria.signupDateFrom,
              signup_date_upto: searchCriteria.signupDateInto,
              with_app: searchCriteria.withApp,
            }),
            email_message: {
              subject: sendEmailData.subject,
              content: sendEmailData.message,
            },
            partner_agent_ids: selectedAgentId,
          };
          if (sendEmailData.schedule && sendEmailData.time) {
            apiParameter.schedule_at = combineDateAndTime(
              sendEmailData.schedule,
              sendEmailData.time,
            );
          }
          sendApiRequest(apiParameter)
            .then((data) => {
              setValidation({
                icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                title: "Success",
                message: "Successfully sent email.",
                open: true,
                actions: (
                  <div className="gap-2 w-full mx-auto">
                    <Button
                      onClick={() => setValidation({ open: false })}
                      value={"OK"}
                      className={"p-2 w-full"}
                    />
                  </div>
                ),
              });
            })
            .catch((e) =>
              setValidation({
                icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                title: "Error",
                message: "Error sending email",
                open: true,
                actions: (
                  <div className="gap-2 w-full mx-auto">
                    <Button
                      onClick={() => setValidation({ open: false })}
                      value={"OK"}
                      className={"p-2 w-full"}
                    />
                  </div>
                ),
              }),
            )
            .finally(() => {
              setSendEmailData((prev) => ({ ...prev, open: false }));
            });
        }}
        onClose={() => {
          setSendEmailData({
            open: false,
            operatorId: localStorage.getItem("user")
              ? JSON.parse(localStorage.getItem("user")).operator_id
              : "",
            message: "",
            target: [],
            subject: "",
          });
        }}
      />
      {/* Popup and Snackbars */}

      <Snackbar
        isOpen={snack.isOpen}
        message={snack.message}
        onClose={() => setSnack((prev) => ({ ...prev, isOpen: false }))}
        type={snack.type}
      />
      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </div>
  );
}
